const pickDate = (obj) =>
    obj?.fechaCaducidad ||
    obj?.fechaValidez ||
    obj?.fechaDeCaducidad ||
    obj?.fechaFin ||
    obj?.finVigencia ||
    null;

/** Diferencia en días, usando como “hoy” una fecha base (la de la cacería) */
const diffDaysFromBase = (baseDate, targetDate) => {
    if (!baseDate || !targetDate) return null;
    const b0 = new Date(baseDate);
    b0.setHours(0, 0, 0, 0);
    const t0 = new Date(targetDate);
    t0.setHours(0, 0, 0, 0);
    return Math.round((t0.getTime() - b0.getTime()) / 86400000);
};

/** Extrae un personId estable venga como venga */
const getPersonId = (per) => {
    if (per == null) return null;
    if (typeof per === "number" || typeof per === "string") return Number(per);

    // objetos típicos
    const id =
        per.id ??
        per.personaId ??
        per.persona ??
        per.personId ??
        null;

    return id != null ? Number(id) : null;
};

/**
 * “Hidrata” persona: si el objeto viene incompleto en la cacería,
 * intenta reemplazarlo por la versión completa de personasCatalogo.
 */
const resolvePersona = (per, personasCatalogo) => {
    const pid = getPersonId(per);
    if (pid == null) return null;

    // Si ya viene completo (tiene arrays típicos), úsalo
    if (typeof per === "object" && (per.licenciasArmas || per.licenciasCaza || per.seguros)) {
        return { ...per, id: per.id ?? pid };
    }

    // Busca en catálogo completo
    const full = (personasCatalogo || []).find((x) => Number(x.id) === pid);
    if (full) return full;

    // Si no hay catálogo, al menos devuelve un objeto con id
    return typeof per === "object" ? { ...per, id: per.id ?? pid } : { id: pid };
};

const buildAlertsForPersonAtDate = (persona, baseDate, horizonDays, caceriaCtx) => {
    const out = [];

    const personId = getPersonId(persona);
    const personName =
        [persona?.nombre, persona?.apellidos].filter(Boolean).join(" ") ||
        persona?.nombre ||
        (personId != null ? `Persona ${personId}` : "—");

    // Documento personal
    const fechaDoc = pickDate(persona);
    if (fechaDoc) {
        const daysLeft = diffDaysFromBase(baseDate, fechaDoc);
        const status = daysLeft < 0 ? "expired" : daysLeft <= horizonDays ? "soon" : "ok";
        if (status !== "ok") {
            out.push({
                id: `C-${caceriaCtx.id}::P-${personId}::DOC`,
                personId,
                personName,
                tipo: "personaDoc",
                label: "Documento",
                meta: `${persona?.tipoDocumentoDescripcion || persona?.tipoDocumento || "Doc."}${persona?.numeroDocumento ? ` • Nº ${persona.numeroDocumento}` : ""
                    }`,
                fecha: fechaDoc,
                daysLeft,
                status,
                caceria: caceriaCtx,
            });
        }
    } else {
        out.push({
            id: `C-${caceriaCtx.id}::P-${personId}::DOC-MISSING`,
            personId,
            personName,
            tipo: "personaDoc",
            label: "Documento",
            meta: `${persona?.tipoDocumentoDescripcion || persona?.tipoDocumento || "Doc."}${persona?.numeroDocumento ? ` • Nº ${persona.numeroDocumento}` : ""
                }`,
            fecha: null,
            daysLeft: null,
            status: "missing",
            caceria: caceriaCtx,
        });
    }

    // Licencias de armas
    (persona?.licenciasArmas || []).forEach((lic, idx) => {
        if (lic?.fechaIndefinida === true) return;
        const fecha = pickDate(lic);
        if (fecha) {
            const daysLeft = diffDaysFromBase(baseDate, fecha);
            const status = daysLeft < 0 ? "expired" : daysLeft <= horizonDays ? "soon" : "ok";
            if (status !== "ok") {
                out.push({
                    id: `C-${caceriaCtx.id}::P-${personId}::LA-${lic.id ?? idx}`,
                    personId,
                    personName,
                    tipo: "licArmas",
                    label: "Licencia de armas",
                    meta: `${lic?.tipoDescripcion || lic?.tipo || ""}${lic?.numero ? ` • Nº ${lic.numero}` : ""}`.trim(),
                    fecha,
                    daysLeft,
                    status,
                    caceria: caceriaCtx,
                });
            }
        } else {
            out.push({
                id: `C-${caceriaCtx.id}::P-${personId}::LA-${lic.id ?? idx}-MISSING`,
                personId,
                personName,
                tipo: "licArmas",
                label: "Licencia de armas",
                meta: `${lic?.tipoDescripcion || lic?.tipo || ""}${lic?.numero ? ` • Nº ${lic.numero}` : ""}`.trim(),
                fecha: null,
                daysLeft: null,
                status: "missing",
                caceria: caceriaCtx,
            });
        }
    });

    // Licencias de caza
    (persona?.licenciasCaza || []).forEach((lic, idx) => {
        const fecha = pickDate(lic);
        if (fecha) {
            const daysLeft = diffDaysFromBase(baseDate, fecha);
            const status = daysLeft < 0 ? "expired" : daysLeft <= horizonDays ? "soon" : "ok";
            if (status !== "ok") {
                out.push({
                    id: `C-${caceriaCtx.id}::P-${personId}::LC-${lic.id ?? idx}`,
                    personId,
                    personName,
                    tipo: "licCaza",
                    label: "Licencia de caza",
                    meta: `${lic?.numero || ""}${lic?.ambito ? ` • ${lic.ambito}` : ""}`.trim(),
                    fecha,
                    daysLeft,
                    status,
                    caceria: caceriaCtx,
                });
            }
        } else {
            out.push({
                id: `C-${caceriaCtx.id}::P-${personId}::LC-${lic.id ?? idx}-MISSING`,
                personId,
                personName,
                tipo: "licCaza",
                label: "Licencia de caza",
                meta: `${lic?.numero || ""}${lic?.ambito ? ` • ${lic.ambito}` : ""}`.trim(),
                fecha: null,
                daysLeft: null,
                status: "missing",
                caceria: caceriaCtx,
            });
        }
    });

    // Seguros
    (persona?.seguros || []).forEach((seg, idx) => {
        const fecha = pickDate(seg);
        if (fecha) {
            const daysLeft = diffDaysFromBase(baseDate, fecha);
            const status = daysLeft < 0 ? "expired" : daysLeft <= horizonDays ? "soon" : "ok";
            if (status !== "ok") {
                out.push({
                    id: `C-${caceriaCtx.id}::P-${personId}::SE-${seg.id ?? idx}`,
                    personId,
                    personName,
                    tipo: "seguro",
                    label: "Seguro",
                    meta: `${seg?.compania || seg?.aseguradora || seg?.compañia || ""}${seg?.numeroPoliza ? ` • Póliza ${seg.numeroPoliza}` : ""
                        }`.trim(),
                    fecha,
                    daysLeft,
                    status,
                    caceria: caceriaCtx,
                });
            }
        } else {
            out.push({
                id: `C-${caceriaCtx.id}::P-${personId}::SE-${seg.id ?? idx}-MISSING`,
                personId,
                personName,
                tipo: "seguro",
                label: "Seguro",
                meta: `${seg?.compania || seg?.aseguradora || seg?.compañia || ""}${seg?.numeroPoliza ? ` • Póliza ${seg.numeroPoliza}` : ""
                    }`.trim(),
                fecha: null,
                daysLeft: null,
                status: "missing",
                caceria: caceriaCtx,
            });
        }
    });

    return out;
};

/**
 * buildAlertsForEvento
 * - personasCatalogo es OPCIONAL pero recomendado para que NO “falte” información.
 */
export const buildAlertsForEvento = (evento, horizonDays, personasCatalogo = []) => {
    const cacerias = Array.isArray(evento?.cacerias) ? evento.cacerias : [];

    // Para deduplicar a nivel de EVENTO (no por cacería)
    const dedup = new Map(); // key -> alert "mejor/peor"

    const getPersonId = (per) =>
        per?.id ?? per?.personaId ?? per?.persona ?? (typeof per === "number" ? per : null);

    const hydratePerson = (per) => {
        const pid = getPersonId(per);
        if (pid == null) return per;
        const full = personasCatalogo.find((x) => Number(x.id) === Number(pid));
        return full || (typeof per === "object" ? per : { id: pid });
    };

    // Prioridad: expired > soon > missing > ok
    const rankStatus = (s) => (s === "expired" ? 0 : s === "soon" ? 1 : s === "missing" ? 2 : 3);

    const keepBest = (prev, next) => {
        // Queremos quedarnos con la "más relevante": peor status, y si empata, menor daysLeft
        const rPrev = rankStatus(prev.status);
        const rNext = rankStatus(next.status);

        if (rNext < rPrev) return next;
        if (rNext > rPrev) return prev;

        const dPrev = prev.daysLeft ?? 9e6;
        const dNext = next.daysLeft ?? 9e6;
        return dNext < dPrev ? next : prev;
    };

    for (const c of cacerias) {
        const baseDate = c?.fecha ? new Date(c.fecha) : null;
        const cCtx = { id: c?.id, fecha: c?.fecha, tipoCaceria: c?.tipoCaceria };

        const parts = Array.isArray(c?.participaciones) ? c.participaciones : [];

        // Las alarmas solo aplican a Cazadores (rol CAZADOR en esta cacería)
        // y a Titulares de Arma (tipoPersona === "TITULAR").
        const cazadorIdsEnEstaCaceria = new Set(
            parts
                .filter((p) => p?.rol?.rol === "CAZADOR")
                .map((p) => getPersonId(p?.persona))
                .filter((pid) => pid != null)
        );

        // Personas involucradas en esa cacería (dedup por persona)
        const persons = new Map();
        const push = (per) => {
            if (!per) return;
            const pid = getPersonId(per);
            if (pid == null) return;
            if (!persons.has(pid)) persons.set(pid, hydratePerson(per));
        };

        for (const p of parts) {
            push(p?.persona);
            push(p?.secretario);
            push(p?.cargador);
        }

        for (const persona of persons.values()) {
            const pid = getPersonId(persona);
            const esCazadorAqui = cazadorIdsEnEstaCaceria.has(pid);
            const esTitular = persona?.tipoPersona === "TITULAR";
            if (!esCazadorAqui && !esTitular) continue;

            const list = buildAlertsForPersonAtDate(persona, baseDate, Number(horizonDays), cCtx) || [];

            for (const a of list) {
                const key = [
                    a.personId ?? getPersonId(persona) ?? "no-person",
                    a.tipo ?? "no-tipo",
                    a.fecha ?? "no-fecha",
                    a.meta ?? "no-meta",
                ].join("::");

                const normalized = { ...a, id: key };

                if (!dedup.has(key)) {
                    dedup.set(key, normalized);
                } else {
                    dedup.set(key, keepBest(dedup.get(key), normalized));
                }
            }
        }
    }

    return Array.from(dedup.values());
};


export const groupAlertsByPerson = (alerts) => {
    const map = new Map();
    alerts.forEach((a) => {
        if (!map.has(a.personId)) {
            map.set(a.personId, { personId: a.personId, personName: a.personName, items: [] });
        }
        map.get(a.personId).items.push(a);
    });

    const rank = (s) => (s === "expired" ? 0 : s === "soon" ? 1 : 2);

    return Array.from(map.values())
        .map((g) => ({
            ...g,
            items: g.items.sort(
                (A, B) => rank(A.status) - rank(B.status) || (A.daysLeft ?? 9e6) - (B.daysLeft ?? 9e6)
            ),
        }))
        .sort((A, B) => (A.personName || "").localeCompare(B.personName || "", "es"));
};
